import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, User } from "lucide-react";
import Logo from "./Logo";

const BASE_NAV = [
  { to: "/app", label: "Today", end: true },
  { to: "/app/meals", label: "Meals" },
  { to: "/app/movement", label: "Movement" },
  { to: "/app/coaching", label: "Coaching" },
  { to: "/app/measurements", label: "Measurements" },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Admin" },
  { to: "/admin/comments", label: "Comments" },
  { to: "/admin/waitlist", label: "Waitlist" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const NAV = user?.role === "admin" ? [...BASE_NAV, ...ADMIN_NAV] : BASE_NAV;
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[hsl(var(--background))]/85 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between">
          <Link to="/app" className="flex items-center gap-2" data-testid="nav-logo">
            <Logo size={80} />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${
                    isActive
                      ? "text-[hsl(var(--foreground))] border-b border-[hsl(var(--primary))] pb-1"
                      : "text-stone-500 hover:text-[hsl(var(--foreground))]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden md:inline text-sm text-stone-500">
              <User className="inline w-4 h-4 mr-1" strokeWidth={1.25} />
              {user?.name}
            </span>
            <button
              onClick={() => { logout(); navigate("/"); }}
              data-testid="btn-logout"
              className="text-sm text-stone-500 hover:text-[hsl(var(--primary))] transition-colors flex items-center gap-1"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.25} /> Sign out
            </button>
          </div>
        </div>
        {/* mobile nav */}
        <div className="md:hidden border-t border-border">
          <div className="max-w-[1400px] mx-auto px-4 flex overflow-x-auto gap-6 py-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`mnav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm whitespace-nowrap ${isActive ? "text-[hsl(var(--primary))]" : "text-stone-500"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16">
        {children}
      </main>
      <footer className="border-t border-border mt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row justify-between gap-4 text-sm text-stone-500">
          <span className="font-serif italic">Energy Awakens — a monthly practice. By <a href="https://satoriom.com" target="_blank" rel="noreferrer" className="not-italic underline hover:text-[hsl(var(--primary))]" data-testid="footer-satoriom-layout">Satori OM</a>.</span>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
